// App.js

import React, { lazy, Suspense, useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, NavLink } from "react-router-dom";
import ReactGA from 'react-ga4';
// Removed motion and AnimatePresence as they are not used in the updated CSS approach
// import { motion, AnimatePresence } from "framer-motion";
import "./styles.css";
import logo from "./Assets/newJimatlyMainLogo.svg"; // Ensure path is correct
import SchemaMarkup from './components/SchemaMarkup';
import NotFoundPage from './components/NotFoundPage';
import GAPageTracker from './components/GAPageTracker';
import BlogIndexPage from './components/BlogIndexPage';
import BlogPostPage from './components/BlogPostPage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import DebtServiceRatioCalculator from "./components/DebtServiceRatioCalculator";

// Lazy Imports... (keep as is)
const AffordabilityPage = lazy(() => import("./AffordabilityPage"));
const RetirementPage = lazy(() => import("./RetirementCalculator"));
const InvestmentPage = lazy(() => import("./InvestmentGrowthSimulator"));
const HomePage = lazy(() => import("./HomePage"));
const PCBCalculator = lazy(() => import("./components/PCBCalculator"));
const BudgetingPage = lazy(() => import("./BudgetingPage"));


function App() {
  // Removed activeTab state as it's not used for active styling anymore
  // const [activeTab, setActiveTab] = useState("Home");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    ReactGA.initialize('G-499X4B6EDY');
    console.log('GA4 initialized with ID: G-499X4B6EDY');
  }, []);

  // Toggle mobile menu state and add/remove class from overlay
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu state
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Handle body scroll lock when menu opens/closes
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    // Cleanup function to remove class if component unmounts while menu is open
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isMobileMenuOpen]); // Re-run effect when isMobileMenuOpen changes


  const navItems = [
    { name: "Home", url: "/" },
    { name: "Affordability", url: "/affordability" },
    { name: "DSR Calculator", url: "/dsr-calculator" },
    { name: "Budgeting", url: "/budgeting" },
    { name: "Retirement", url: "/retirement" },
    { name: "Investment", url: "/investment" },
    { name: "PCB Calculator", url: "/pcb" },
    { name: "Blog", url: "/blog" },
  ];

  return (
    <Router>
      <SchemaMarkup />
      <GAPageTracker />
      {/* app-container padding adjusted in CSS */}
      <div className="app-container">

        {/* Main Navigation Bar */}
        <nav className="navbar-new">
          {/* Added inner container for padding control */}
          <div className="navbar-inner-container">
            {/* Logo Image directly inside navbar */}
            <NavLink to="/" className="navbar-logo-link" aria-label="Go to Homepage">
              <img src={logo} alt="Jimatly Logo" className="navbar-logo" />
            </NavLink>

            {/* Desktop Navigation Links */}
            <div className="navbar-container desktop-nav">
              {navItems.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.url}
                  // Use NavLink's isActive property provided by the function
                  className={({ isActive }) =>
                    `nav-item ${isActive ? "active" : ""}`
                  }
                  // Apply 'end' prop specifically to the Home link
                  end={item.url === "/"}
                >
                  <span className="nav-item-text">{item.name}</span>
                  {/* Render active pill based on NavLink's isActive */}
                  {({ isActive }) => isActive && (
                    <div className="active-pill" /> // Using simple div, animation handled by CSS if desired
                  )}
                </NavLink>
              ))}
            </div>

             {/* Hamburger button - Placed after desktop nav for flexbox ordering */}
             <button className="hamburger-button" onClick={toggleMobileMenu} aria-label="Toggle navigation menu">
               {/* Toggle icon based on state */}
               <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
             </button>
          </div>
        </nav>

        {/* Mobile Menu Overlay - Controlled by class */}
        {/* Removed AnimatePresence, using CSS transitions */}
        <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
          <button className="mobile-menu-close-button" onClick={closeMobileMenu} aria-label="Close navigation menu">
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div className="mobile-menu-container">
            {/* Map over navItems to create mobile links */}
            {navItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.url}
                onClick={closeMobileMenu} // Close menu on link click
                // Use NavLink's isActive property
                className={({ isActive }) =>
                  `nav-item mobile-nav-item ${isActive ? "active" : ""}`
                }
                // Apply 'end' prop specifically to the Home link
                end={item.url === "/"}
              >
                <span className="nav-item-text">{item.name}</span>
              </NavLink>
            ))}
          </div>
        </div>


        {/* Page Content Area */}
        {/* Added class for styling */}
        <Suspense fallback={<div className="loading-placeholder">Loading page...</div>}>
          <Routes>
             <Route path="/" element={<HomePage />} />
             <Route path="/affordability" element={<AffordabilityPage />} />
             <Route path="/retirement" element={<RetirementPage />} />
             <Route path="/investment" element={<InvestmentPage />} />
             <Route path="/pcb" element={<PCBCalculator />} />
             <Route path="/blog" element={<BlogIndexPage />} />
             <Route path="/blog/:slug" element={<BlogPostPage />} />
             <Route path="/budgeting" element={<BudgetingPage />} />
             <Route path="/dsr-calculator" element={<DebtServiceRatioCalculator />} />
             <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>

        {/* Footers */}
        <footer className="about-me">
            <h3>About Me</h3>
            <p>
              Hello! I'm just a regular guy with <strong>ZERO coding skills</strong> 😅.
            </p>
            <p>
              This app is my little passion project — built entirely by myself (yes, even though I
              still have no idea how to code - THANK YOU CHATGPT & YOUTUBE!!).
            </p>
            <p>
              Why did I make this? Because I believe everyone; regardless how much they earn, deserves
              to understand money.
            </p>
            <p>For support or inquiries, you can email me at <a href="mailto:support@jimatly.com">support@jimatly.com</a>.</p>
            <p>
              <br /><strong>Like what you see? Support my work!</strong>
              <br />
              <br />
              <a href="https://www.buymeacoffee.com/jimatly" target="_blank" rel="noopener noreferrer">
                <img src="https://img.buymeacoffee.com/button-api/?text=Buy me a coffee&emoji=☕&slug=jimatly&button_colour=40DCA5&font_colour=ffffff&font_family=Inter&outline_colour=000000&coffee_colour=FFDD00"
                  alt="Buy me a coffee" />
              </a>
            </p>
        </footer>

        <footer className="disclaimer">
             <h3>Disclaimer</h3>
             <p>
               The information and calculations provided by this app are for informational purposes
               only and should not be considered as financial advice. While we strive for accuracy, we
               do not guarantee the correctness of the results or their applicability to your
               individual circumstances. Use of this app is at your own risk, and we disclaim any
               liability for decisions made based on the outcomes generated by this app. Please
               consult a qualified financial advisor for personalized advice.
             </p>
        </footer>
      </div> {/* End app-container */}
    </Router>
  );
}

export default App;
